import * as React from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Input,
  ModalClose,
  Skeleton,
  Stack,
} from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import Typography from "@mui/joy/Typography";
import { useTranslation } from "../../../i18n";
import {
  TeamRole,
  useTeamAddUserMutation,
  useTeamInfoQuery,
  useUserBySlugQuery,
} from "../../../__generated__/types-and-hooks";
import { useNotify } from "../../../hooks/useNotify";
import UserAvatar from "../../../components/UserAvatar";
import { CountryFlag } from "../../../components/CountryFlag";

export interface AddUserModalProps {
  open: boolean;
}

export default function AddUserModal({ open }: AddUserModalProps) {
  const notify = useNotify();

  const [teamAddUser] = useTeamAddUserMutation({});

  const [slug, setSlug] = React.useState("");

  const handleChangeEmail = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setSlug(e.target.value);
    },
    [setSlug]
  );

  const userBySlugQuery = useUserBySlugQuery({ variables: { slug } });

  const navigate = useNavigate();
  const t = useTranslation();
  const { teamSlug } = useParams();
  const teamInfoQuery = useTeamInfoQuery({ variables: { slug: teamSlug! } });
  const team = teamInfoQuery.data?.team_by_slug;

  const [saving, setSaving] = React.useState(false);

  const user = userBySlugQuery.data?.user_by_slug;
  const handleInvite = React.useCallback(async () => {
    if (!user) return;

    setSaving(true);
    try {
      await teamAddUser({
        variables: {
          teamId: team?.id!,
          userId: user.id,
          roles: [TeamRole.Member],
        },
        onCompleted() {
          notify({
            type: "success",
            msg: "A new member has been added successfully.",
          });
          navigate("..");
        },
      });
    } finally {
      setSaving(false);
    }
  }, [team, user, teamAddUser, notify, navigate]);

  return (
    <Modal open={open}>
      <ModalDialog>
        <ModalClose
          onClick={() =>
            navigate("..", {
              relative: "path",
            })
          }
        />
        <DialogTitle>{t("Invite Members")}</DialogTitle>
        <DialogContent>
          <Stack gap={4} sx={{ mt: 2 }}>
            <Typography>
              {t("You're inviting members to {team}", {
                team: team?.name ?? team?.slug ?? "",
              })}
            </Typography>
            <FormControl required>
              <FormLabel>{t("Please enter a user slug")}</FormLabel>
              <Input
                size="md"
                onChange={handleChangeEmail}
                placeholder="slug"
              />
            </FormControl>

            {user && (
              <Card orientation="horizontal">
                <span>
                  <UserAvatar userId={user.id} size="sm" />
                </span>
                <CardContent>
                  <Typography level="title-lg" id="card-description">
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography
                    level="body-sm"
                    aria-describedby="card-description"
                    sx={{ mb: 1 }}
                  >
                    <CountryFlag code={user.countryCode} />
                  </Typography>
                </CardContent>
              </Card>
            )}
            {!user && (
              <Card orientation="horizontal">
                <span>
                  <Skeleton
                    animation="wave"
                    variant="circular"
                    width={32}
                    height={32}
                  />
                </span>
                <CardContent>
                  <Typography level="title-lg" id="card-description">
                    -- / --
                  </Typography>
                  <Typography
                    level="body-sm"
                    aria-describedby="card-description"
                    sx={{ mb: 1 }}
                  >
                    -- / --
                  </Typography>
                </CardContent>
              </Card>
            )}
            <Stack gap={4} sx={{ mt: 2 }}>
              <Button
                disabled={!user || saving}
                loading={saving}
                type="submit"
                fullWidth
                onClick={handleInvite}
              >
                {t("Add")}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}
